<template>
  <b-field
    :label="label"
    :type="errorMessage !== '' ? 'is-danger' : ''" 
    :message="errorMessage"
  >
    <b-input
      data-test-id="phoneNumberInput"
      expanded
      v-model="phoneNumber"
      :placeholder="canEdit ? placeholder : ''"
      :disabled="canEdit === false"
    />
  </b-field>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  AsYouType,
  parsePhoneNumberFromString,
  isValidPhoneNumber
} from "libphonenumber-js/mobile"

interface Props {
  modelValue?: string | number
  canEdit?: boolean
  label?: string
  placeholder?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: 0,
  canEdit: true,
  label: '',
  placeholder: ''
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: number): void
}>()

const { t } = useI18n()

const phoneNumberValue = ref<string | number>(props.canEdit ? parseInt(String(props.modelValue)) : props.modelValue)

const errorMessage = computed(() => {
  if (props.canEdit === false) {
    return ""
  }
  const isValid = phoneNumber.value ? isValidPhoneNumber(String(phoneNumber.value), "IL") : null
  if (isValid === false) {
    return t('FieldIsInvalid', { fieldName: props.label })
  } else {
    return ""
  }
})

const phoneNumber = computed({
  get() {
    if (phoneNumberValue.value && props.canEdit) {
      const asYouType = new AsYouType("IL")
      asYouType.input(phoneNumberValue.value.toString())
      const number = asYouType.getNumber()
      return number ? number.formatNational() : phoneNumberValue.value.toString()
    }
    return phoneNumberValue.value ?? 0
  },
  set(editedPhoneNumber: string | number) {
    const parsed = parsePhoneNumberFromString(String(editedPhoneNumber), "IL")
    phoneNumberValue.value = parsed ? parsed.number : 0
  },
})

watch(phoneNumberValue, (newValue) => {
  emit("update:modelValue", parseInt(String(newValue)))
}, { immediate: true })

// Watch for prop changes
watch(() => props.modelValue, (newValue) => {
  phoneNumberValue.value = props.canEdit ? parseInt(String(newValue)) : newValue
})
</script>
